import { Injectable } from '@angular/core';
import { ExportCreativeDialogService, CreativeExport } from '@app/manage-view/export-creative';
import { CreativesetDataService } from '@app/shared/creativeset';
import { FileDownloadService } from '@app/shared/services';
import { UINotificationService } from '@bannerflow/ui';
import { ICreative } from '@studio/domain/creativeset/creative/creative';
import {
    IExportCreativeFrame,
    IExportCreativeItem,
    IExportCreativeRequest,
    IExportSettings,
    IExportVideoRequest,
    IExportVideoCreative
} from '@studio/domain/creativeset/creative/export-creative';
import { ICreativeset } from '@studio/domain/creativeset/creativeset';
import { Logger } from '@studio/utils/logger';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ExportCreativeDataService } from '../export-creative.data.service';
import { IGifFrame, GifExport } from './gif-export';

@Injectable()
export class ExportService {
    private downloadIsPending = false;
    private logger = new Logger('ExportService');

    private _isExporting$ = new BehaviorSubject<boolean>(false);
    isExporting$ = this._isExporting$.asObservable();

    constructor(
        private creativesetData: CreativesetDataService,
        private exportCreativeDataService: ExportCreativeDataService,
        private exportCreativeDialogService: ExportCreativeDialogService,
        private fileDownloadService: FileDownloadService,
        private notificationService: UINotificationService
    ) {
        this.exportCreativeDataService.exportFinished$.subscribe(({ filePath }) => {
            this.fileDownloadService.download(filePath);
        });

        this.exportCreativeDataService.exportFailure$.subscribe(({ failures }) => {
            const message = failures?.length ? 'Could not export creative' : 'Error while exporting';

            this.notificationService.open(message, { type: 'error', autoCloseDelay: 5000 });
        });

        this.exportCreativeDialogService.dialogClosed$.subscribe(settings => {
            if (settings) {
                const creativeset = this.creativesetData.creativeset;
                if (settings.type === 'mp4') {
                    this.startVideoExport(creativeset, settings);
                } else {
                    this.startImageExport(creativeset, settings);
                }
            }
        });
    }

    set isExporting(exporting: boolean) {
        this._isExporting$.next(exporting);
    }

    private async startImageExport(
        creativeset: ICreativeset,
        settings: IExportSettings
    ): Promise<void> {
        if (this.downloadIsPending) {
            return;
        }
        this.logger.verbose('Starting to export creatives');

        this.notificationService.open(
            'Images are being exported - download will start once they are ready.',
            {
                autoCloseDelay: 5000,
                placement: 'top',
                type: 'success'
            }
        );

        const creatives = settings.creatives;
        const exportCreatives: IExportCreativeItem[] = creatives
            .map(creative => ({
                creativeId: creative.id,
                frames: this.getFramesFromCreative(creative, settings)
            }))
            .filter(e => e.frames.length > 0);

        const loops = settings.loops ?? 0;

        const request: IExportCreativeRequest = {
            type: settings.type,
            quality: settings.quality,
            loops: loops,
            creatives: exportCreatives,
            creativesetId: creativeset.id
        };

        return this.export(creativeset.brandId, request);
    }

    private async export(brandId: string, request: IExportCreativeRequest): Promise<void> {
        const creativeExport = new CreativeExport();
        creativeExport.creativesetId = request.creativesetId;

        const res = await firstValueFrom(
            this.exportCreativeDataService.exportCreative(brandId, request)
        );
        if (!res) {
            throw new Error('Error while exporting');
        }

        this.logger.verbose(`Export requested. CorrelationId: ${res.correlationId}`);
        creativeExport.correlationId = res.correlationId;
        creativeExport.startTime = Date.now();

        this.exportCreativeDataService.checkExportStatus(creativeExport);
    }

    private async startVideoExport(
        creativeset: ICreativeset,
        settings: IExportSettings
    ): Promise<void> {
        const request = this.createVideoRequest(creativeset, settings);
        this.exportCreativeDataService.exportCreativeVideo(request);
    }

    private createVideoRequest(
        creativeset: ICreativeset,
        settings: IExportSettings
    ): IExportVideoRequest {
        return {
            creativeSetId: +creativeset.id,
            creatives: this.createCreativeVideoRequestInput([...settings.creatives], settings.frameRate)
        };
    }

    private createCreativeVideoRequestInput(
        creatives: ICreative[],
        frameRate: number = 60
    ): IExportVideoCreative[] {
        const exportCreatives: IExportVideoCreative[] = [];
        for (const creative of creatives) {
            if (!creative.design) {
                continue;
            }
            exportCreatives.push({
                width: creative.size.width,
                height: creative.size.height,
                duration: creative.design.document.duration,
                id: +creative.id,
                fps: frameRate
            });
        }
        return exportCreatives;
    }

    private getFramesFromCreative(
        creative: ICreative,
        settings?: IExportSettings
    ): IExportCreativeFrame[] {
        if (!creative.design?.document) {
            throw new Error(`Can't calculate where image should be captured without provided document`);
        }

        if (settings?.type === 'gif') {
            const gifExport = new GifExport(creative.design.document);
            let gifFrames: IGifFrame[] = [];

            switch (settings.gifFrameLimitMode) {
                case 'file-size':
                    gifFrames = gifExport.getFramesByFileSize(
                        settings.fileSizeLimit || 300,
                        settings.quality
                    );
                    break;
                case 'frame-count':
                    gifFrames = gifExport.getFrames(settings.frameLimit);
                    break;
                case 'manual':
                    gifFrames = gifExport.getManualFrames();
                    break;
            }

            // Remove bloated properties
            if (gifFrames.length) {
                return gifFrames.map(frame => ({
                    time: frame.time,
                    duration: typeof frame.duration === 'number' ? frame.duration : 4
                }));
            }
        }
        return [
            {
                time: creative.design.document.getFirstPreloadImageFrame(),
                duration: 4
            }
        ];
    }
}
